import React from 'react';
import { useTheme } from 'styled-components';
import NextImage from 'next/image';

import useBreakpoint from 'Common/hooks/useBreakpoint';
import { GraduateStoryCanvasProps, MappedBlock, isJSXElement } from 'Common/util/cmsCanvas';
import type { ImageVideoHeroBannerProps } from 'Common/util/cmsComposer/HeroBanner';

import Section from 'Elements/Section';
import Main from 'Elements/Main';
import { H2 } from 'Elements/Typography';
import Icon, { IconButtonProps } from 'Elements/Button/Icon';
import VisuallyHidden from 'Elements/VisuallyHidden';
import FlexBox from 'Elements/FlexBox';

import Grid, { Col } from 'Components/Grid';
import Card, { CardProps } from 'Components/Card';
import Breadcrumbs, { BreadcrumbsProps } from 'Components/Breadcrumbs';
import Carousel, { CarouselProps } from 'Components/Carousel';
import SocialIcons, { SocialIconsProps } from 'Components/SocialIcons';
import SolentStory, { SolentStoryCardProps } from 'Components/Card/SolentStory';
import ImageCaption from 'Components/ImageCaption';
import Video from 'Components/Video';
import Wave from 'Components/QuoteBlock/Wave';

import VideoHeroBanner from 'Modules/HeroBanner/MakeWaves/Video';
import ImageHeroBanner from 'Modules/HeroBanner/MakeWaves/Image';
import { SocialWrapper } from 'Modules/PromoPoint/Socials/Socials.styled';

import FadeAnimation from 'Templates/Clearing/FadeAnimation';

import { DefaultGroupWrapper } from './Story.styled';

export interface Props {
    breadcrumbs: BreadcrumbsProps;
    heroBanner: ImageVideoHeroBannerProps;
    canvasContent?: MappedBlock<GraduateStoryCanvasProps>[];
    shareLinks: {
        heading: string;
        socialIcons: SocialIconsProps;
    };
    cTAs?: IconButtonProps[];
    courseStudiedCard?: CardProps;
    relatedCourseCards?: {
        carousel: CarouselProps;
    };
    moreStories?: {
        heading: string;
        cards: SolentStoryCardProps[];
    };
}

const Story: React.FC<Props> = props => {
    const {
        breadcrumbs,
        heroBanner,
        canvasContent,
        shareLinks,
        cTAs,
        courseStudiedCard,
        relatedCourseCards,
        moreStories
    } = props;

    const { colors, breakpoints, commonStyles } = useTheme();

    const isDesktop = useBreakpoint(breakpoints.large.value);
    const isMobileOnly = !useBreakpoint(breakpoints.medium.value);

    const moreStoriesFirstThree = moreStories ? moreStories.cards.slice(0, 3) : undefined;
    const moreStoriesThreeOrFour = moreStories
        ? isDesktop || isMobileOnly
            ? moreStoriesFirstThree
            : moreStories.cards
        : undefined;

    const WhiteHorizontalRule: React.FC = () => {
        return (
            <hr
                style={{
                    height: `${commonStyles.brandPixel}`,
                    color: `${colors.secondary.tints[1].hex}`,
                    margin: 0
                }}
            />
        );
    };

    const renderContent = (
        item: MappedBlock<GraduateStoryCanvasProps>,
        index: number
    ): JSX.Element | undefined => {
        const columnStartLeftOrRightDesktop = index % 2 === 0 ? 1 : 6;
        const columnEndLeftOrRightDesktop = index % 2 === 0 ? 9 : 13;

        const columnStartLeftOrRightTablet = index % 2 === 0 ? 1 : 7;
        const columnEndLeftOrRightTablet = index % 2 === 0 ? 11 : 13;

        const topMargin = index === 0 ? 0 : '2rem';

        const ComposerItemWrapper: React.FC<{
            columnStart?: number;
            columnEnd?: number;
            columnStartTablet?: number;
            columnEndTablet?: number;
        }> = props => {
            const {
                children,
                columnStart = columnStartLeftOrRightDesktop,
                columnEnd = columnEndLeftOrRightDesktop,
                columnStartTablet = columnStartLeftOrRightTablet,
                columnEndTablet = columnEndLeftOrRightTablet
            } = props;

            return (
                <Grid style={{ marginTop: topMargin, marginBottom: '2rem' }}>
                    <Col l={[columnStart, columnEnd]} m={[columnStartTablet, columnEndTablet]}>
                        <div>{children}</div>
                    </Col>
                </Grid>
            );
        };

        if (item.groupName === '_defaultGroup' && isJSXElement(item.elements)) {
            return (
                <ComposerItemWrapper>
                    <DefaultGroupWrapper>
                        {item.elements.map(element => (
                            <React.Fragment key={element.key}>{element}</React.Fragment>
                        ))}
                    </DefaultGroupWrapper>
                </ComposerItemWrapper>
            );
        }

        if (!isJSXElement(item.elements)) {
            const component = item.elements;

            return (
                <>
                    {component.image && (
                        <ComposerItemWrapper>
                            <ImageCaption {...component.image.imageCaption} />
                        </ComposerItemWrapper>
                    )}
                    {component.imageCanvas && (
                        <div
                            style={{
                                marginTop: topMargin,
                                marginBottom: '2rem',
                                display: 'flex',
                                justifyContent: 'center'
                            }}
                        >
                            <FadeAnimation
                                transformInView="translateY(0px)"
                                transformOutView="translateY(50px)"
                            >
                                <NextImage
                                    src={component.imageCanvas.src}
                                    alt={component.imageCanvas.alt ? component.imageCanvas.alt : ''}
                                    width={1920}
                                    height={780}
                                    style={{
                                        objectFit: 'cover',
                                        maxWidth: '100%',
                                        height: 'auto'
                                    }}
                                />
                            </FadeAnimation>
                        </div>
                    )}
                    {component.video && (
                        <ComposerItemWrapper>
                            <Video {...component.video.embed} />
                        </ComposerItemWrapper>
                    )}
                    {component.quoteBlock && (
                        <FadeAnimation
                            transformInView="translateX(0%)"
                            transformOutView="translateX(-75%)"
                        >
                            <Wave {...component.quoteBlock} />
                        </FadeAnimation>
                    )}
                    {component.carousel && (
                        <ComposerItemWrapper columnStartTablet={1} columnEndTablet={13}>
                            <Carousel
                                {...component.carousel}
                                colours={{
                                    primary: `${colors.secondary.hex}`,
                                    secondary: `${colors.secondary.tints[8].hex}`
                                }}
                            />
                        </ComposerItemWrapper>
                    )}
                </>
            );
        }
    };

    return (
        <Main>
            <div
                style={{
                    background: `radial-gradient(90rem at -20rem -50rem, ${colors.secondaryContrast.hex} 50%, ${colors.secondary.tints[8].hex} 100%)`
                }}
            >
                <Section id="story-hero-banner" paddingTop={0} paddingBottom={0}>
                    <Breadcrumbs {...breadcrumbs} />
                    {heroBanner.video && (
                        <VideoHeroBanner
                            {...heroBanner.video}
                            backgroundColour="transparent"
                            showWave={false}
                        />
                    )}
                    {heroBanner.image && (
                        <ImageHeroBanner
                            {...heroBanner.image}
                            backgroundColour="transparent"
                            showWave={false}
                            imageQuality={100}
                        />
                    )}
                </Section>
                {canvasContent && (
                    <Section id="story-canvas" paddingTop={1}>
                        <div style={{ color: colors.secondary.hex }}>
                            {canvasContent.map((item, index) => (
                                <React.Fragment key={index}>
                                    {renderContent(item, index)}
                                </React.Fragment>
                            ))}
                        </div>
                    </Section>
                )}
                <Section
                    id="story-social-links"
                    paddingBottom={1.5}
                    colour={colors.primaryContrast.hex}
                >
                    <Grid>
                        <Col>
                            <H2 color={colors.secondary.tints[1].hex} weight={300}>
                                {shareLinks.heading}
                            </H2>
                            <SocialWrapper style={{ marginTop: '1rem' }}>
                                <SocialIcons {...shareLinks.socialIcons} />
                            </SocialWrapper>
                        </Col>
                    </Grid>
                </Section>
                <WhiteHorizontalRule />
                {cTAs && (
                    <Section id="story-calls-to-action" paddingTop={1.5} paddingBottom={1.5}>
                        <Grid>
                            <Col>
                                <H2 color={colors.secondary.tints[1].hex} weight={300}>
                                    Start making your own waves
                                </H2>
                                <FlexBox
                                    wrap="wrap"
                                    gap={'1.5rem'}
                                    cols={{ small: 1, medium: 2, large: 3 }}
                                    asHtmlElement="ul"
                                >
                                    {cTAs.map((cta, index) => (
                                        <li key={`call-to-action-list-button-${index}`}>
                                            <Icon {...cta} />
                                        </li>
                                    ))}
                                </FlexBox>
                            </Col>
                        </Grid>
                    </Section>
                )}
                {cTAs && <WhiteHorizontalRule />}
                {(courseStudiedCard || relatedCourseCards) && (
                    <Section
                        id="story-studied-and-related-courses"
                        paddingTop={1.5}
                        paddingBottom={1.5}
                    >
                        {courseStudiedCard &&
                        isDesktop &&
                        relatedCourseCards &&
                        relatedCourseCards.carousel.slides.length > 2 ? (
                            <Grid>
                                <Col b={4}>
                                    <H2 color={colors.accents[6].hex} weight={300}>
                                        Course studied
                                    </H2>
                                    <div style={{ color: colors.secondary.tints[1].hex }}>
                                        <Card {...courseStudiedCard} />
                                    </div>
                                </Col>
                                <Col b={8}>
                                    <H2 color={colors.secondary.tints[1].hex} weight={300}>
                                        Related courses
                                    </H2>
                                    <Carousel
                                        {...relatedCourseCards.carousel}
                                        colours={{
                                            primary: `${colors.secondary.hex}`,
                                            secondary: `${colors.secondary.tints[8].hex}`
                                        }}
                                    />
                                </Col>
                            </Grid>
                        ) : (
                            <Grid>
                                {courseStudiedCard && (
                                    <Col b={6} l={4}>
                                        <H2
                                            aria-hidden="true"
                                            color={colors.accents[6].hex}
                                            weight={300}
                                        >
                                            Course studied
                                        </H2>
                                    </Col>
                                )}
                                {relatedCourseCards && !isMobileOnly && (
                                    <Col b={6} l={8}>
                                        <H2
                                            aria-hidden="true"
                                            color={colors.secondary.tints[1].hex}
                                            weight={300}
                                        >
                                            Related courses
                                        </H2>
                                    </Col>
                                )}
                                <Col>
                                    <FlexBox
                                        gap="1.5rem"
                                        wrap="wrap"
                                        cols={{ small: 1, medium: 2, large: 3 }}
                                        asHtmlElement="ul"
                                    >
                                        {courseStudiedCard && (
                                            <>
                                                <VisuallyHidden>
                                                    <H2 color={colors.accents[6].hex}>
                                                        Course studied
                                                    </H2>
                                                </VisuallyHidden>
                                                <li
                                                    style={{
                                                        color: colors.secondary.tints[1].hex
                                                    }}
                                                    key={`course-studied-card`}
                                                >
                                                    <Card {...courseStudiedCard} />
                                                </li>
                                            </>
                                        )}
                                        {relatedCourseCards && (
                                            <>
                                                {!isMobileOnly && (
                                                    <VisuallyHidden>
                                                        <H2 color={colors.secondary.tints[1].hex}>
                                                            Related courses
                                                        </H2>
                                                    </VisuallyHidden>
                                                )}
                                                {isMobileOnly && (
                                                    <H2
                                                        color={colors.secondary.tints[1].hex}
                                                        weight={300}
                                                    >
                                                        Related courses
                                                    </H2>
                                                )}
                                                {relatedCourseCards.carousel.slides.map(
                                                    (card, index) => (
                                                        <li key={`related-course-card-${index}`}>
                                                            {card.element}
                                                        </li>
                                                    )
                                                )}
                                            </>
                                        )}
                                    </FlexBox>
                                </Col>
                            </Grid>
                        )}
                    </Section>
                )}
                {moreStories && <WhiteHorizontalRule />}
                {moreStories && moreStoriesThreeOrFour && (
                    <Section id="story-more-stories" paddingTop={1.5} paddingBottom={2}>
                        <Grid>
                            <Col>
                                <H2 color={colors.secondary.tints[1].hex} weight={300}>
                                    {moreStories.heading}
                                </H2>
                                <FlexBox
                                    gap={{ small: '2.5rem', large: '1.5rem' }}
                                    wrap="wrap"
                                    cols={{ small: 1, medium: 2, large: 3 }}
                                    asHtmlElement="ul"
                                >
                                    {moreStoriesThreeOrFour.map((card, index) => (
                                        <li key={`more-stories-card-${index}`}>
                                            <SolentStory {...card} />
                                        </li>
                                    ))}
                                </FlexBox>
                            </Col>
                        </Grid>
                    </Section>
                )}
            </div>
        </Main>
    );
};

export default Story;
